package horcrux.common.api.dtos

import arrow.core.Either
import arrow.core.NonEmptyList
import arrow.core.raise.either
import arrow.core.right
import horcrux.common.HorcruxRequestCompositeError
import horcrux.common.HorcruxRequestError
import horcrux.common.HorcruxRequestSimpleError
import horcrux.common.NonEmptyMapSerializer
import horcrux.common.URLValueClass
import horcrux.common.api.dtos.RemoteRawDocumentIdentifier.Companion.instagramAccountURLRegex
import horcrux.common.api.dtos.RemoteRawDocumentIdentifier.Companion.instagramPostURLRegex
import horcrux.common.api.dtos.RemoteRawDocumentIdentifier.Companion.validateURLPattern
import horcrux.common.api.dtos.types.DocumentType
import horcrux.common.api.dtos.types.DocumentURL
import horcrux.common.api.dtos.types.RawDocumentID
import horcrux.common.api.dtos.types.RawDocumentName
import horcrux.common.api.dtos.types.UserID
import horcrux.common.types.JsonTransmittable
import horcrux.common.types.NonEmptyMap
import horcrux.common.utils.Validatable
import horcrux.common.utils.toEither
import horcrux.common.utils.toOptional
import io.ktor.http.HttpStatusCode
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlin.jvm.JvmInline

@Serializable
sealed interface RawDocumentIdentifier : Validatable<RawDocumentIdentifier> {
    val rawDocumentName: RawDocumentName
    val rawDocumentClientSideID: RawDocumentClientSideID
}

@Serializable
sealed interface RemoteRawDocumentIdentifier : RawDocumentIdentifier {
    val documentURL: DocumentURL
    override fun validate(): Either<PostRawDocumentsSimpleError, RawDocumentIdentifier> = either {
        documentURL.validate()
            .mapLeft { InvalidURLPattern(rawDocumentClientSideID, documentURL, URLValueClass.urlRegex) }.bind()
        this@RemoteRawDocumentIdentifier
    }

    companion object {
        operator fun invoke(
            rawDocumentClientSideID: RawDocumentClientSideID,
            documentURL: DocumentURL,
            rawDocumentName: RawDocumentName
        ): RemoteRawDocumentIdentifier =
            when {
                // TODO: uncomment this when support for InstagramPost DocumentType is introduced
//                documentURL.matchesURLPattern(instagramPostURLRegex) -> InstagramPostRawDocumentIdentifier(
//                    rawDocumentClientSideID, documentURL, rawDocumentName
//                )

                documentURL.matchesURLPattern(instagramAccountURLRegex) -> InstagramAccountRawDocumentIdentifier(
                    rawDocumentClientSideID, documentURL, rawDocumentName
                )

                else -> URLRawDocumentIdentifier(rawDocumentClientSideID, documentURL, rawDocumentName)
            }.also { println(it) }

        val instagramAccountURLRegex: Regex
            get() = Regex("""https://www\.instagram\.com/([\w.-]+)/""")
        val instagramPostURLRegex: Regex
            get() = Regex("""https://www\.instagram\.com/p/([\w.-]+)/?""")

        fun DocumentURL.matchesURLPattern(regex: Regex): Boolean = regex.matches(this.betterToString())

        fun DocumentURL.validateURLPattern(
            rawDocumentClientSideID: RawDocumentClientSideID,
            regex: Regex
        ): Either<PostRawDocumentsSimpleError, Unit> =
            matchesURLPattern(regex).toOptional().toEither()
                .mapLeft { InvalidURLPattern(rawDocumentClientSideID, this, regex) }
    }
}

@Serializable
data class URLRawDocumentIdentifier(
    override val rawDocumentClientSideID: RawDocumentClientSideID,
    override val documentURL: DocumentURL,
    override val rawDocumentName: RawDocumentName,
) : RemoteRawDocumentIdentifier {
    override fun validate(): Either<PostRawDocumentsSimpleError, URLRawDocumentIdentifier> = either {
        super.validate()
        this@URLRawDocumentIdentifier
    }
}

@Serializable
data class InstagramAccountRawDocumentIdentifier(
    override val rawDocumentClientSideID: RawDocumentClientSideID,
    override val documentURL: DocumentURL,
    override val rawDocumentName: RawDocumentName
) : RemoteRawDocumentIdentifier {
    override fun validate(): Either<PostRawDocumentsSimpleError, InstagramAccountRawDocumentIdentifier> = either {
        super.validate()
        documentURL.validateURLPattern(rawDocumentClientSideID, instagramAccountURLRegex).bind()
        this@InstagramAccountRawDocumentIdentifier
    }
}

@Serializable
data class InstagramPostRawDocumentIdentifier(
    override val rawDocumentClientSideID: RawDocumentClientSideID,
    override val documentURL: DocumentURL,
    override val rawDocumentName: RawDocumentName
) : RemoteRawDocumentIdentifier {
    override fun validate(): Either<PostRawDocumentsSimpleError, InstagramPostRawDocumentIdentifier> = either {
        super.validate()
        documentURL.validateURLPattern(rawDocumentClientSideID, instagramPostURLRegex).bind()
        this@InstagramPostRawDocumentIdentifier
    }
}

@Serializable
@JvmInline
value class LocalDocument(val byteArray: ByteArray) {
    override fun toString(): String = "LocalDocument<numBytes=${byteArray.size}>"
}

@Serializable
data class LocalRawDocumentIdentifier(
    override val rawDocumentClientSideID: RawDocumentClientSideID,
    val localDocument: LocalDocument,
    override val rawDocumentName: RawDocumentName
) : RawDocumentIdentifier {
    override fun validate(): Either<Throwable, LocalRawDocumentIdentifier> = right()
}

@JvmInline
@Serializable
value class RawDocumentClientSideID(val value: Int) : Comparable<RawDocumentClientSideID> {
    override fun compareTo(other: RawDocumentClientSideID): Int = value.compareTo(other.value)

}

object RawDocumentIdentifiersByIDSerializer : NonEmptyMapSerializer<RawDocumentClientSideID, RawDocumentIdentifier> {
    override val keySerializer: KSerializer<RawDocumentClientSideID> = RawDocumentClientSideID.serializer()
    override val valueSerializer: KSerializer<RawDocumentIdentifier> = RawDocumentIdentifier.serializer()
}

@Serializable
data class PostRawDocumentsRequest(@Serializable(RawDocumentIdentifiersByIDSerializer::class) val rawDocumentIdentifiersByID: NonEmptyMap<RawDocumentClientSideID, RawDocumentIdentifier>) :
    HorcruxRequest {
    override fun validate(): Either<Throwable, PostRawDocumentsRequest> = either {
        rawDocumentIdentifiersByID.values.forEach { it.validate().bind() }
        this@PostRawDocumentsRequest
    }
}

@Serializable
data class RawDocumentMetadata(
    val rawDocumentID: RawDocumentID,
    val documentType: DocumentType,
    val documentURL: DocumentURL,
)

object RawDocumentMetadataByClientSideIDSerializer :
    NonEmptyMapSerializer<RawDocumentClientSideID, RawDocumentMetadata> {
    override val keySerializer: KSerializer<RawDocumentClientSideID> = RawDocumentClientSideID.serializer()
    override val valueSerializer: KSerializer<RawDocumentMetadata> = RawDocumentMetadata.serializer()
}

@Serializable
data class PostRawDocumentsResponse(
    @Serializable(RawDocumentMetadataByClientSideIDSerializer::class) val rawDocumentMetadataClientSideID: NonEmptyMap<RawDocumentClientSideID, RawDocumentMetadata>
) : HorcruxResponse {
    val rawDocumentMetadataClientSideIDList: NonEmptyList<Pair<RawDocumentClientSideID, RawDocumentMetadata>>
        get() = rawDocumentMetadataClientSideID.toNonEmptyList()
}

@Serializable
sealed interface PostRawDocumentsErrorType {
    val errorMessage: String

    @Serializable
    data object ErrorDetectingRawDocumentType : PostRawDocumentsErrorType {
        override val errorMessage: String = "There was an error detecting this document type"
    }

    @Serializable
    data object ErrorGettingMimeTypeFromURL : PostRawDocumentsErrorType {
        override val errorMessage: String = "No document was found at this url"
    }

    @Serializable
    data class UnsupportedRawDocumentType(val mimeType: String) : PostRawDocumentsErrorType {
        override val errorMessage: String = "Document type $mimeType is unsupported"
    }

    @Serializable
    data object ErrorPersistingRawDocument : PostRawDocumentsErrorType {
        override val errorMessage: String = "Sorry, an unexpected error occurred when uploading your document."
    }

    @Serializable
    data object ErrorDownloadingDocument : PostRawDocumentsErrorType {
        override val errorMessage: String = "There was an error downloading this document. Please make sure it exists."
    }

    @Serializable
    data object ErrorProcessingRawDocuments : PostRawDocumentsErrorType {
        override val errorMessage: String = "An error occurred when processing your documents"
    }

    @Serializable
    data object InvalidURLPatten : PostRawDocumentsErrorType {
        override val errorMessage: String = "An invalid URL pattern was provided for this raw document type"
    }
}

sealed interface PostRawDocumentsError : HorcruxRequestError {
    override val httpStatusCode: HttpStatusCode
    val postRawDocumentsErrorType: PostRawDocumentsErrorType
}

sealed class PostRawDocumentsSimpleError(
    httpStatusCode: HttpStatusCode,
    reasonMaybe: HorcruxErrorReason? = null,
    causeMaybe: Throwable? = null,
) : HorcruxRequestSimpleError(null, causeMaybe, httpStatusCode, reasonMaybe), PostRawDocumentsError {
    abstract override val postRawDocumentsErrorType: PostRawDocumentsErrorType
    abstract val rawDocumentClientSideID: RawDocumentClientSideID
}

sealed class PostRawDocumentsCompositeError(
    httpStatusCode: HttpStatusCode,
    reasonMaybe: HorcruxErrorReason? = null,
    causes: List<Throwable>,
) : HorcruxRequestCompositeError(null, causes, httpStatusCode, reasonMaybe), PostRawDocumentsError {
    abstract override val postRawDocumentsErrorType: PostRawDocumentsErrorType
}

data class ErrorDetectingRawDocumentType(
    override val rawDocumentClientSideID: RawDocumentClientSideID, val userID: UserID, override val cause: Throwable
) : PostRawDocumentsSimpleError(HttpStatusCode.UnsupportedMediaType) {
    override val postRawDocumentsErrorType: PostRawDocumentsErrorType =
        PostRawDocumentsErrorType.ErrorDetectingRawDocumentType
}

data class ErrorGettingMimeTypeFromURL(
    val url: String,
    override val rawDocumentClientSideID: RawDocumentClientSideID,
    val userID: UserID,
    override val cause: Throwable
) : PostRawDocumentsSimpleError(HttpStatusCode.NotFound) {
    override val postRawDocumentsErrorType: PostRawDocumentsErrorType =
        PostRawDocumentsErrorType.ErrorGettingMimeTypeFromURL
}

data class UnsupportedRawDocumentType(
    override val rawDocumentClientSideID: RawDocumentClientSideID,
    val mimeType: String,
    val userID: UserID,
    override val cause: Throwable
) : PostRawDocumentsSimpleError(HttpStatusCode.UnsupportedMediaType) {
    override val postRawDocumentsErrorType: PostRawDocumentsErrorType =
        PostRawDocumentsErrorType.UnsupportedRawDocumentType(mimeType)
}

data class ErrorPersistingRawDocument(
    override val rawDocumentClientSideID: RawDocumentClientSideID,
    val rawDocumentID: RawDocumentID,
    val documentType: DocumentType,
    val user: UserID,
    override val cause: Throwable,
) : PostRawDocumentsSimpleError(HttpStatusCode.BadGateway) {
    override val postRawDocumentsErrorType: PostRawDocumentsErrorType =
        PostRawDocumentsErrorType.ErrorPersistingRawDocument
}

data class ErrorDownloadingRawDocument(
    override val rawDocumentClientSideID: RawDocumentClientSideID,
    val url: String,
    val userID: UserID,
    override val cause: Throwable?,
) : PostRawDocumentsSimpleError(HttpStatusCode.NotFound) {
    override val postRawDocumentsErrorType: PostRawDocumentsErrorType =
        PostRawDocumentsErrorType.ErrorDownloadingDocument
}

data class InvalidURLPattern(
    override val rawDocumentClientSideID: RawDocumentClientSideID,
    val documentURL: DocumentURL,
    val regex: Regex,
) : PostRawDocumentsSimpleError(HttpStatusCode.BadRequest) {
    override val postRawDocumentsErrorType: PostRawDocumentsErrorType =
        PostRawDocumentsErrorType.ErrorDownloadingDocument
}

data class RawDocumentCacheError(
    override val rawDocumentClientSideID: RawDocumentClientSideID,
    val user: UserID,
    override val cause: HorcruxRequestSimpleError
) :
    PostRawDocumentsSimpleError(cause.httpStatusCode) {
    override val postRawDocumentsErrorType: PostRawDocumentsErrorType =
        PostRawDocumentsErrorType.ErrorProcessingRawDocuments
}

data class ErrorProcessingRawDocuments(
    val causeByClientSideID: Map<RawDocumentClientSideID, PostRawDocumentsSimpleError>,
    val userID: UserID,
    val firstHttpStatusCode: HttpStatusCode,
) : PostRawDocumentsCompositeError(
    firstHttpStatusCode,
    PostRawDocumentsErrorReason(causeByClientSideID),
    causeByClientSideID.map { it.value },
) {
    override val postRawDocumentsErrorType: PostRawDocumentsErrorType =
        PostRawDocumentsErrorType.ErrorProcessingRawDocuments
}

@Serializable
data class PostRawDocumentsErrorReason(val errorTypeByClientSideID: Map<RawDocumentClientSideID, PostRawDocumentsErrorType>) :
    HorcruxErrorReason {
    override val jsonString: String
        get() = serializeToJsonString(serializer(), this)

    companion object : JsonTransmittable.Companion<PostRawDocumentsErrorReason> {
        operator fun invoke(causeByClientSideID: Map<RawDocumentClientSideID, PostRawDocumentsSimpleError>) =
            PostRawDocumentsErrorReason(causeByClientSideID.map { it.key to it.value.postRawDocumentsErrorType }
                .toMap())

        override fun deserializeFromByteArray(byteArray: ByteArray): Either<Throwable, PostRawDocumentsErrorReason> =
            deserializeFromBase64Json(serializer(), byteArray)

    }
}
